import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, TrendingUp, Activity, Flame, Info, AlertTriangle, CheckCircle2, Mail, Loader2, ShieldCheck } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { LineChart, BarChart } from "../components/MiniChart";
import { loadDiary, diaryStats, generateInsights } from "../phase2";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const toneStyle = {
  info: { icon: Info, bg: "#e8f4fc", fg: "#2b7fd9" },
  warn: { icon: AlertTriangle, bg: "#fdeee8", fg: "#e08a2b" },
  good: { icon: CheckCircle2, bg: "#eafaf1", fg: "#22a35a" },
};

export default function Insights() {
  const stats = useMemo(() => diaryStats(loadDiary()), []);
  const fallback = useMemo(() => generateInsights(stats), [stats]);
  const [insights, setInsights] = useState(fallback);
  const [aiLoading, setAiLoading] = useState(stats.count > 0);
  const [isAi, setIsAi] = useState(false);

  useEffect(() => {
    if (stats.count === 0) { setAiLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/ai/insights`, {
          avg_severity: stats.avgSeverity,
          flares: stats.flares,
          count: stats.count,
          top_symptoms: stats.top.map((s) => s.label),
          trend: stats.severitySeries,
        });
        if (!cancelled && data.source === "ai" && Array.isArray(data.insights) && data.insights.length) {
          setInsights(data.insights);
          setIsAi(true);
        }
      } catch (e) { console.warn("AI insights unavailable, using rules-based fallback:", e?.message); }
      if (!cancelled) setAiLoading(false);
    })();
    return () => { cancelled = true; };
  }, [stats]);

  return (
    <AppLayout title="AI Insights">
      <div className="bg-gradient-to-r from-[#0d9488] to-[#0f766e] rounded-2xl p-6 text-white flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center"><Sparkles size={26} /></div>
        <div><h2 className="font-head font-bold text-xl">Your personalised insights</h2><p className="text-white/85 text-sm mt-0.5">Generated from your symptom diary and connected wearables.</p></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5"><div className="flex items-center gap-2 text-slate-500 text-[13px] font-medium"><Activity size={16} className="text-[#0d9488]" /> Avg severity</div><p className="text-2xl font-head font-bold text-slate-800 mt-1">{stats.avgSeverity}/5</p></div>
        <div className="bg-white rounded-xl border border-slate-200 p-5"><div className="flex items-center gap-2 text-slate-500 text-[13px] font-medium"><Flame size={16} className="text-[#e0567a]" /> Episodes logged</div><p className="text-2xl font-head font-bold text-slate-800 mt-1">{stats.flares}</p></div>
        <div className="bg-white rounded-xl border border-slate-200 p-5"><div className="flex items-center gap-2 text-slate-500 text-[13px] font-medium"><TrendingUp size={16} className="text-[#2b7fd9]" /> Entries analysed</div><p className="text-2xl font-head font-bold text-slate-800 mt-1">{stats.count}</p></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-head font-semibold text-slate-800 mb-3">Recent severity trend</h3>
          <LineChart data={stats.severitySeries.length ? stats.severitySeries : [0]} max={5} />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-head font-semibold text-slate-800 mb-3">Most frequent symptoms</h3>
          {stats.top.length ? (
            <div className="space-y-2">
              {stats.top.map((s) => (
                <div key={s.id} className="flex items-center gap-3">
                  <span className="text-[13px] text-slate-600 w-40 truncate">{s.label}</span>
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#0d9488] rounded-full" style={{ width: `${(s.count / stats.top[0].count) * 100}%` }} /></div>
                  <span className="text-[12px] font-semibold text-slate-500 w-6 text-right">{s.count}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">No symptoms logged yet.</p>}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-8 mb-3">
        <h3 className="font-head font-semibold text-slate-800">Insights for you</h3>
        {isAi && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#6d4bd8] bg-[#f0ecfb] px-2 py-0.5 rounded-full"><Sparkles size={12} /> AI generated</span>}
        {aiLoading && <span className="inline-flex items-center gap-1 text-[12px] text-slate-400"><Loader2 size={13} className="animate-spin" /> personalising…</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {insights.map((ins, i) => {
          const st = toneStyle[ins.tone] || toneStyle.info;
          const Icon = st.icon;
          return (
            <div key={`${ins.title}-${i}`} className="bg-white rounded-xl border border-slate-200 p-5 flex gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: st.bg, color: st.fg }}><Icon size={20} /></div>
              <div><h4 className="font-semibold text-slate-800">{ins.title}</h4><p className="text-[13.5px] text-slate-500 mt-1 leading-relaxed">{ins.text}</p></div>
            </div>
          );
        })}
      </div>

      <p className="text-[12px] text-slate-400 flex items-center gap-1.5 mt-6"><ShieldCheck size={13} /> Insights are stored in your account and are informational only — not a medical diagnosis.</p>
    </AppLayout>
  );
}
